import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ModalOverlay,
    ModalContainer,
    CloseButton,
    PromoImage,
    ContentWrapper,
    Badge,
    Title,
    Subtitle,
    Description,
    ButtonsContainer,
    PrimaryButton,
    SecondaryButton,
    EffectsLayer
} from './PromoModal.styled';
import { SnowEffect, HeartsEffect, ConfettiEffect } from './effects';

const PromoModal = ({ isOpen, onClose, config }) => {
    const navigate = useNavigate();
    const closeButtonRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            closeButtonRef.current?.focus();
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    const handleOverlayClick = useCallback((e) => {
        if (config.styling.closeOnOverlayClick && e.target === e.currentTarget) {
            onClose();
        }
    }, [config.styling.closeOnOverlayClick, onClose]);

    const handlePrimaryAction = useCallback(() => {
        const { type, target } = config.primaryAction;

        switch (type) {
            case 'route':
                const hasPromoProducts = !!config.productPrefix;
                const targetUrl = hasPromoProducts
                    ? `${target}?promo=${config.id}`
                    : target;
                navigate(targetUrl);
                onClose();
                break;
            case 'link':
                window.open(target, '_blank', 'noopener,noreferrer');
                onClose();
                break;
            case 'close':
            default:
                onClose();
        }
    }, [config, navigate, onClose]);

    const renderEffect = () => {
        const { effectType, effectIntensity } = config;
        const effectProps = { intensity: effectIntensity };

        switch (effectType) {
            case 'snow':
                return <SnowEffect {...effectProps} />;
            case 'hearts':
                return <HeartsEffect {...effectProps} />;
            case 'confetti':
                return <ConfettiEffect {...effectProps} />;
            default:
                return null;
        }
    };

    if (!isOpen) return null;

    const { content, styling, primaryAction, secondaryAction } = config;

    return (
        <ModalOverlay
            $opacity={styling.overlayOpacity}
            $blur={styling.backdropBlur}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-label="Promocion especial"
        >
            <EffectsLayer>
                {renderEffect()}
            </EffectsLayer>

            <ModalContainer
                $maxWidth={styling.modalMaxWidth}
                $borderRadius={styling.borderRadius}
                $animationDuration={styling.animationDuration}
                onClick={(e) => e.stopPropagation()}
            >
                {styling.showCloseButton && (
                    <CloseButton
                        ref={closeButtonRef}
                        onClick={onClose}
                        aria-label="Cerrar promocion"
                    >
                        &times;
                    </CloseButton>
                )}

                {content.image?.src && (
                    <PromoImage
                        src={content.image.src}
                        alt={content.image.alt}
                        $aspectRatio={content.image.aspectRatio}
                        loading="eager"
                    />
                )}

                <ContentWrapper>
                    {content.badge?.enabled && (
                        <Badge $color={content.badge.color}>
                            {content.badge.text}
                        </Badge>
                    )}

                    {content.title && <Title>{content.title}</Title>}
                    {content.subtitle && <Subtitle>{content.subtitle}</Subtitle>}
                    {content.description && <Description>{content.description}</Description>}

                    <ButtonsContainer>
                        <PrimaryButton
                            onClick={handlePrimaryAction}
                            $style={primaryAction.style}
                        >
                            {primaryAction.label}
                        </PrimaryButton>

                        {secondaryAction && (
                            <SecondaryButton onClick={onClose}>
                                {secondaryAction.label}
                            </SecondaryButton>
                        )}
                    </ButtonsContainer>
                </ContentWrapper>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default PromoModal;
